import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { DataService } from '@/data/DataService';
import { seedSnapshot } from '@/data/seed';
import { listClients } from '@/services/clientsService';
import { DataSnapshot, CURRENT_SCHEMA, ID } from '@/types/domain';

import { AppDispatch, RootState } from '../index';

type CreateClientInput = {
  name: string;
  email: string;
  phone?: string;
  company?: string;
  mrr?: number;
  billingStatus?: 'On-Time' | 'Late' | 'Paused';
  healthScore?: number;
};

type CreateCaseInput = {
  clientId: ID;
  title: string;
  status?: 'investigation' | 'pre-trial' | 'in-court' | 'closed' | 'intake';
  priority?: 'low' | 'med' | 'high';
  nextDate?: string;
  progress?: number;
};

type CreateWhiteboardInput = {
  title: string;
  template?: 'blank' | 'strategy' | 'evidence' | 'contract';
  clientId?: ID;
  caseId?: ID;
};

interface DomainState {
  snapshot: DataSnapshot;
}

const initialState: DomainState = {
  snapshot: {
    version: CURRENT_SCHEMA,
    tenant: null,
    users: [],
    clients: [],
    cases: [],
    whiteboards: [],
  },
};

const domainSlice = createSlice({
  name: 'domain',
  initialState,
  reducers: {
    setSnapshot(state, action: PayloadAction<DataSnapshot>) {
      state.snapshot = action.payload;
    },
    importSnapshot(state, action: PayloadAction<DataSnapshot>) {
      state.snapshot = { ...action.payload, version: CURRENT_SCHEMA };
    },
    setCases(state, action: PayloadAction<any[]>) {
      const incoming = Array.isArray(action.payload) ? action.payload : ([] as any[]);
      const normalizeCase = (k: any) => {
        const id = String(k?.id ?? k?._id ?? k?.caseId ?? '');
        const title = k?.title || 'Untitled Case';
        const status = String(k?.status || '').toLowerCase();
        const priority = String(k?.priority || 'medium').toLowerCase();
        const nextDate = k?.nextDate || k?.dueDate || k?.openedAt || k?.openDate || '';
        const clientId = String(k?.clientId || '');
        const clientName = k?.client || k?.clientName || '';
        const createdAt = k?.createdAt || new Date().toISOString();
        return {
          id,
          title,
          status,
          priority,
          nextDate,
          clientId,
          client: clientName,
          createdAt,
        } as any;
      };
      const normalized = incoming.map(normalizeCase);
      state.snapshot.cases = normalized;
    },
    setClients(state, action: PayloadAction<any[]>) {
      const incoming = Array.isArray(action.payload) ? action.payload : ([] as any[]);
      const normalizedIncoming = incoming.map((c: any) => {
        const id = String(c?.id ?? c?._id ?? c?.clientId ?? '');
        const derivedName =
          c?.name ||
          c?.clientName ||
          c?.companyName ||
          c?.contactPerson ||
          c?.mainPointOfContact?.name ||
          '';
        const email = c?.email ?? c?.mainPointOfContact?.email ?? '';
        const company = c?.company ?? c?.companyName ?? c?.contactPerson ?? email ?? '';
        return {
          ...c,
          id,
          name: derivedName,
          email,
          company,
          createdAt: c?.createdAt || c?.updatedAt || new Date().toISOString(),
        };
      });
      const existing = Array.isArray(state.snapshot.clients) ? state.snapshot.clients : [];
      const byId = new Map<string, any>(existing.map((c: any) => [String(c.id), c]));
      normalizedIncoming.forEach((c: any) =>
        byId.set(String(c.id), { ...(byId.get(String(c.id)) || {}), ...c })
      );
      state.snapshot.clients = Array.from(byId.values());
    },
    upsertClient(state, action: PayloadAction<any>) {
      const input = action.payload as any;
      const normalizedId = String(input?.id ?? input?._id ?? input?.clientId ?? '');
      if (!normalizedId) return;
      const normalized = {
        ...input,
        id: normalizedId,
        name:
          input?.name ||
          input?.companyName ||
          input?.clientName ||
          input?.mainPointOfContact?.name ||
          'Unnamed',
        email: input?.email || input?.mainPointOfContact?.email || '',
        company:
          input?.company ||
          input?.companyName ||
          input?.contactPerson ||
          input?.mainPointOfContact?.email ||
          input?.email ||
          '',
        createdAt: input?.createdAt || new Date().toISOString(),
      };
      const idx = (state.snapshot.clients ?? []).findIndex(
        (c: any) => String(c.id) === String(normalizedId)
      );
      if (idx >= 0) {
        state.snapshot.clients[idx] = { ...state.snapshot.clients[idx], ...normalized } as any;
      } else {
        state.snapshot.clients = [normalized, ...(state.snapshot.clients ?? [])] as any[];
      }
    },
  },
});

export const { setSnapshot, importSnapshot, setCases, setClients, upsertClient } =
  domainSlice.actions;

export const selectSnapshot = (state: RootState) =>
  ((state as any).domain?.snapshot?.snapshot as DataSnapshot) ?? seedSnapshot; // Fixed: double snapshot path
export const selectClients = (state: RootState) => selectSnapshot(state).clients ?? [];
export const selectCases = (state: RootState) => selectSnapshot(state).cases ?? [];
export const selectBoards = (state: RootState) => selectSnapshot(state).whiteboards ?? [];

// Helpers
const getTenantId = (state: RootState): string => {
  const tid = (state as any)?.auth?.user?.tenantId || (state as any)?.auth?.tenantId;
  return tid || (state as any)?.organization?.currentTenantId || 'default-tenant';
};

// Thunks
export const fetchClientsThunk =
  () => async (dispatch: AppDispatch, _getState: () => RootState) => {
    try {
      const payload = await listClients();
      const items = Array.isArray((payload as any)?.items)
        ? (payload as any).items
        : Array.isArray(payload)
          ? payload
          : [];
      console.log(
        '📦 fetchClientsThunk: received items',
        Array.isArray(items) ? items.length : 'invalid'
      );
      if (Array.isArray(items) && items.length > 0) {
        console.log('📦 fetchClientsThunk: sample item', {
          id: items[0]?.id ?? items[0]?._id,
          name: items[0]?.name,
          email: items[0]?.email ?? items[0]?.mainPointOfContact?.email,
          type: items[0]?.type,
          companyName: items[0]?.companyName,
        });
      }
      dispatch(setClients(Array.isArray(items) ? items : []));
    } catch (err: any) {
      // Silently fail; UI can surface via global error handling if needed
    }
  };

export const createClient =
  (input: CreateClientInput) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const base = selectSnapshot(state);
    const snap: DataSnapshot = {
      ...base,
      clients: [...(base.clients ?? [])],
      cases: [...(base.cases ?? [])],
      whiteboards: [...(base.whiteboards ?? [])],
    };
    const tenantId = getTenantId(state);
    const id = crypto?.randomUUID ? crypto.randomUUID() : String(Date.now());
    const createdAt = new Date().toISOString();
    snap.clients.unshift({
      id,
      tenantId,
      name: input.name,
      email: input.email,
      phone: input.phone,
      company: input.company,
      mrr: input.mrr ?? 0,
      billingStatus: input.billingStatus ?? 'On-Time',
      healthScore: input.healthScore ?? 86,
      createdAt,
    } as any);
    await DataService.save(snap);
    dispatch(setSnapshot(snap));
  };

export const createCase =
  (input: CreateCaseInput) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const base = selectSnapshot(state);
    const snap: DataSnapshot = {
      ...base,
      clients: [...(base.clients ?? [])],
      cases: [...(base.cases ?? [])],
      whiteboards: [...(base.whiteboards ?? [])],
    };
    const tenantId = getTenantId(state);
    const id = crypto?.randomUUID ? crypto.randomUUID() : String(Date.now());
    const createdAt = new Date().toISOString();
    const client = snap.clients.find(c => c.id === input.clientId) as any;
    snap.cases.unshift({
      id,
      tenantId,
      clientId: input.clientId,
      title: input.title,
      status: (input.status ?? 'investigation') as any,
      priority: (input.priority ?? 'med') as any,
      nextDate: input.nextDate,
      progress: input.progress ?? 5,
      createdAt,
      // compatibility for UI adapters
      // @ts-ignore
      client: client?.name || 'Unknown Client',
      // @ts-ignore
      dueDate: input.nextDate,
    } as any);
    await DataService.save(snap);
    dispatch(setSnapshot(snap));
  };

export const updateCase =
  (
    id: ID,
    patch: Partial<{
      title: string;
      status: 'investigation' | 'pre-trial' | 'in-court' | 'closed' | 'intake';
      priority: 'low' | 'med' | 'high';
      nextDate: string;
      progress: number;
    }>
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const base = selectSnapshot(state);
    const snap: DataSnapshot = {
      ...base,
      clients: [...(base.clients ?? [])],
      cases: [...(base.cases ?? [])],
      whiteboards: [...(base.whiteboards ?? [])],
    };
    snap.cases = snap.cases.map(c => (c.id === id ? { ...c, ...patch } : c)) as any;
    await DataService.save(snap);
    dispatch(setSnapshot(snap));
  };

export const createWhiteboard =
  (input: CreateWhiteboardInput) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const base = selectSnapshot(state);
    const snap: DataSnapshot = {
      ...base,
      clients: [...(base.clients ?? [])],
      cases: [...(base.cases ?? [])],
      whiteboards: [...(base.whiteboards ?? [])],
    };
    const tenantId = getTenantId(state);
    const id = crypto?.randomUUID ? crypto.randomUUID() : String(Date.now());
    const createdAt = new Date().toISOString();
    snap.whiteboards.unshift({
      id,
      tenantId,
      title: input.title,
      template: input.template ?? 'blank',
      clientId: input.clientId,
      caseId: input.caseId,
      createdAt,
    } as any);
    await DataService.save(snap);
    dispatch(setSnapshot(snap));
  };

export const assignWhiteboardToClient =
  (whiteboardId: ID, clientId: ID) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const base = selectSnapshot(state);
    const snap: DataSnapshot = {
      ...base,
      clients: [...(base.clients ?? [])],
      cases: [...(base.cases ?? [])],
      whiteboards: [...(base.whiteboards ?? [])],
    };
    snap.whiteboards = snap.whiteboards.map(wb =>
      wb.id === whiteboardId ? { ...wb, clientId } : wb
    ) as any;
    await DataService.save(snap);
    dispatch(setSnapshot(snap));
  };

export const assignWhiteboardToCase =
  (whiteboardId: ID, caseId: ID) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const base = selectSnapshot(state);
    const snap: DataSnapshot = {
      ...base,
      clients: [...(base.clients ?? [])],
      cases: [...(base.cases ?? [])],
      whiteboards: [...(base.whiteboards ?? [])],
    };
    snap.whiteboards = snap.whiteboards.map(wb =>
      wb.id === whiteboardId ? { ...wb, caseId } : wb
    ) as any;
    await DataService.save(snap);
    dispatch(setSnapshot(snap));
  };

export const exportSnapshot = () => (_dispatch: AppDispatch, getState: () => RootState) => {
  const snap = selectSnapshot(getState());
  try {
    const blob = new Blob([JSON.stringify(snap, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `legaltech-snapshot-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch {
    // no-op
  }
};

export const importSnapshotThunk = (data: DataSnapshot) => async (dispatch: AppDispatch) => {
  const migrated = { ...data, version: CURRENT_SCHEMA } as DataSnapshot;
  dispatch(importSnapshot(migrated));
  await DataService.save(migrated);
};

export default domainSlice.reducer;
